import {
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';

import { AssistantApi } from '../../apis';
import { QueryKeys } from '../query-keys';
import useAssistantStatus from './use-assistant-status';
import { CONVERSATION_QUERY_KEY } from './use-conversation';

const SEND_MESSAGE_MUTATION_KEY = [QueryKeys.ASSISTANT, 'send-message'];

interface Answer {
  /** What the assistant has written so far. */
  text: string;
}

const NO_ANSWER: Answer = { text: '' };

const useSendMessage = () => {
  const queryClient = useQueryClient();
  const [answer, setAnswer] = useState(NO_ANSWER);
  const assistantStatus = useAssistantStatus();

  const mutation = useMutation({
    mutationKey: SEND_MESSAGE_MUTATION_KEY,
    // A failed reply is reported through the result instead of being thrown: the shared query
    // client would turn a thrown 5xx or timeout into the full-page error for the whole app.
    mutationFn: async (content: string) => {
      try {
        for await (const event of AssistantApi.streamMessage(content)) {
          switch (event.type) {
            case 'delta':
              assistantStatus.answerStarted();
              setAnswer((current) => ({
                ...current,
                text: current.text + event.text,
              }));
              break;
            case 'tool':
              switch (event.state) {
                case 'RUNNING':
                  assistantStatus.toolStarted(event.name);
                  break;
                case 'DONE':
                  assistantStatus.toolFinished();
                  break;
                case 'FAILED':
                  assistantStatus.toolFailed();
                  break;
              }
              break;
            case 'done':
              return true;
            case 'error':
              return false;
          }
        }
        return false;
      } catch {
        return false;
      } finally {
        // However the stream ended, the answer is over: that does not wait for the conversation to reload.
        assistantStatus.answerEnded();
      }
    },
    // Returned, so the message only counts as sent once the conversation has been reloaded. The
    // backend stores the question even when the reply fails, so it is reloaded either way.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: CONVERSATION_QUERY_KEY }),
    // After onSuccess, so the written text does not vanish before the stored message shows up.
    onSettled: () => setAnswer(NO_ANSWER),
  });

  // Read from the shared mutation cache, not from this hook instance, so a remount while a reply
  // is pending does not unblock sending.
  const pending = useMutationState({
    filters: { mutationKey: SEND_MESSAGE_MUTATION_KEY, status: 'pending' },
    select: (pendingMutation) => pendingMutation.state.variables as string,
  });

  return {
    /** Resolves to whether the assistant replied. */
    sendMessage: mutation.mutateAsync,
    isSending: pending.length > 0,
    /** The message whose reply is still pending, if any. */
    pendingContent: pending[0] ?? null,
    /** The reply as it is being written; empty until the assistant starts. */
    answerText: answer.text,
    /** What to show of the assistant until it writes; each status is shown for at least a second. */
    status: assistantStatus.status,
  };
};

export default useSendMessage;
